import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StandardPage from '../../../components/StandardPage';
import { useOrganization } from '../../../context/organization';
import { DatapointPricingSettings, DatapointServicePricing, DatapointSourcePricing } from '../../../models/datapoint-pricing';
import { handleApiError } from '../../../api-client/error_handler';
import axios from 'axios';
import { trackDatapointPricingSaved } from '../../../analytics';

const cloneSettings = (settings: DatapointPricingSettings): DatapointPricingSettings =>
  JSON.parse(JSON.stringify(settings));

const DatapointPricingAdminPage = () => {
  const { organization } = useOrganization();
  const { getAccessTokenSilently } = useAuth0();
  const [settings, setSettings] = useState<DatapointPricingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isCorporateUser = organization?.me.is_corporate;

  const services = useMemo<DatapointServicePricing[]>(() => {
    if (!settings) return [];
    return Object.values(settings.services).sort((a, b) => a.label.localeCompare(b.label));
  }, [settings]);

  useEffect(() => {
    if (!isCorporateUser) {
      setLoading(false);
      return;
    }
    const loadSettings = async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/datapoint-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(data.settings);
      } catch (error) {
        toast.error(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [getAccessTokenSilently, isCorporateUser]);

  const updateSourceValue = (serviceKey: string, sourceKey: string, value: number) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const next = cloneSettings(prev);
      const service = next.services[serviceKey];
      if (!service) return prev;
      const source = service.sources[sourceKey];
      if (!source) return prev;
      source.credits_per_point = value;
      return next;
    });
  };

  const updateVariantValue = (serviceKey: string, sourceKey: string, variantKey: string, value: number) => {
    setSettings((prev) => {
      if (!prev) return prev;
      const next = cloneSettings(prev);
      const service = next.services[serviceKey];
      if (!service) return prev;
      const source = service.sources[sourceKey];
      if (!source || !source.variants) return prev;
      const variant = source.variants[variantKey];
      if (!variant) return prev;
      variant.credits_per_point = value;
      return next;
    });
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/datapoint-pricing`,
        { settings },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setSettings(data.settings);
      trackDatapointPricingSaved();
      toast.success('Pricing settings updated.');
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!isCorporateUser) {
    return (
      <StandardPage title="Datapoint Pricing" marginBottom={40}>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Restricted</h3>
          <p className="mt-2 text-sm text-slate-600">Only CivAPI administrators can access these settings.</p>
        </div>
      </StandardPage>
    );
  }

  return (
    <StandardPage title="Datapoint Pricing" marginBottom={40}>
      <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-blue-900">Credits Conversion</h3>
        <p className="mt-1 text-sm text-blue-800">
          Define how many credits are issued per euro. This value is used in the dashboard and Stripe checkout flows.
        </p>
        {settings ? (
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <label htmlFor="eur-to-credits" className="text-sm font-medium text-blue-900">
              Credits per EUR
            </label>
            <input
              id="eur-to-credits"
              type="number"
              min={1}
              step={100}
              value={settings.eur_to_credits}
              onChange={(e) =>
                setSettings((prev) => (prev ? { ...prev, eur_to_credits: Math.max(1, Number(e.target.value)) } : prev))
              }
              className="w-full rounded-lg border border-blue-200 px-3 py-2 text-blue-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-60"
            />
          </div>
        ) : (
          <p className="mt-3 text-sm text-blue-800">Loading…</p>
        )}
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">Loading pricing…</div>
      ) : !settings ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          Unable to load settings.
        </div>
      ) : (
        <div className="space-y-6">
          {services.map((service) => (
            <div key={service.key} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h4 className="text-lg font-semibold text-slate-900">{service.label}</h4>
              <p className="text-sm text-slate-500">Configure the credit cost per datapoint for each source.</p>

              <div className="mt-4 space-y-4">
                {Object.values(service.sources)
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((source: DatapointSourcePricing) => (
                    <div key={source.key} className="rounded-xl border border-slate-100 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <p className="text-base font-semibold text-slate-900">{source.label}</p>
                          <p className="text-xs uppercase tracking-wide text-slate-500">{source.key}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-medium text-slate-500">Base</label>
                          <input
                            type="number"
                            min={0}
                            value={source.credits_per_point}
                            onChange={(e) => updateSourceValue(service.key, source.key, Math.max(0, Number(e.target.value)))}
                            className="w-28 rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                          />
                          <span className="text-xs text-slate-500">cr / datapoint</span>
                        </div>
                      </div>

                      {source.variants && (
                        <div className="mt-3 space-y-2 border-l border-dashed border-slate-200 pl-4">
                          {Object.values(source.variants).map((variant) => (
                            <div key={variant.key} className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                              <span className="font-medium">{variant.label}</span>
                              <input
                                type="number"
                                min={0}
                                value={variant.credits_per_point}
                                onChange={(e) =>
                                  updateVariantValue(service.key, source.key, variant.key, Math.max(0, Number(e.target.value)))
                                }
                                className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-sm"
                              />
                              <span className="text-xs text-slate-500">cr / datapoint</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </StandardPage>
  );
};

export default DatapointPricingAdminPage;
